/* oxlint-disable max-lines-per-function */
import { useCallback, useEffect, useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useLocationSearch } from '#src/app/pages/search/shared/hooks';
import type { LocationItem, LocationItemTypeEnum } from '#src/app/pages/search/shared/types';

type Result = ModelHookResult<
	{
		data: {
			isLoading: boolean;
			isError: boolean;
			locations: readonly LocationItem[];
			selectedKey: string;
			defaultSelectedKey: string;
		};
		searchTerm: string;
	},
	{
		handleInputFocus: () => void;
		handleInputChange: (value: string) => void;
		handleInputBlur: () => void;
		selectLocation: (searchValue: string) => void;
	}
>;

export function useLocationFilter(): Result {
	const track = useTracking();

	const {
		data: { locations, isLoading, isError, selectedLocation },
		actions: { searchDebounced, selectLocation },
	} = useLocationSearch();

	const [searchTerm, setSearchTerm] = useState('');
	const [defaultSelectedKey] = useState<string>(selectedLocation || '');

	useEffect(() => {
		if (!selectedLocation && searchTerm) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setSearchTerm('');
		}
		if (selectedLocation && selectedLocation !== searchTerm) {
			setSearchTerm(selectedLocation);
		}
		// Sync input value with location state (e.g., when query params change externally)
		// TODO: Review this logic after full migration to Typesense client
		// oxlint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedLocation]);

	return {
		data: {
			data: {
				isLoading,
				isError,
				locations,
				selectedKey: selectedLocation || '',
				defaultSelectedKey,
			},
			searchTerm,
		},
		actions: {
			handleInputFocus: useCallback(() => {
				void track('search_location_filter_enter');
			}, [track]),
			handleInputChange: useCallback(
				(value: string) => {
					if (!value) {
						setSearchTerm('');
						return;
					}
					void track('search_location_filter_search', { search_term: value });
					searchDebounced(value);
					setSearchTerm(value);
				},
				[searchDebounced, track],
			),
			handleInputBlur: useCallback(() => {
				// TODO: Review this logic after full migration to Typesense client
				if (searchTerm === '' && selectedLocation) {
					setSearchTerm(selectedLocation);
				}
			}, [searchTerm, selectedLocation]),
			selectLocation: useCallback(
				(selectedKey: string) => {
					if (!selectedKey) {
						// Prevent setting empty selection
						// If the user clears the input, we don't want to trigger a selection with an empty value
						// This prevents an infinite loop
						return;
					}
					const location = locations.find((loc) => loc.value === selectedKey);
					const type = location?.type as LocationItemTypeEnum;
					// TODO: Review this logic after full migration to Typesense client
					selectLocation(location?.value || '', type);
				},
				[locations, selectLocation],
			),
		},
	};
}
